<script setup lang="ts">
import {
  ChartBarIcon,
  ChartPieIcon,
  ArrowPathIcon,
  HashtagIcon,
  PlusIcon,
  XMarkIcon,
  PresentationChartLineIcon,
  SignalIcon,
  BellAlertIcon,
  ChartBarSquareIcon,
  PresentationChartBarIcon,
  LockClosedIcon,
  CalculatorIcon,
} from '@heroicons/vue/24/outline'
import { useI18n } from '@/composable/useI18n'
import type { WidgetType } from '@/interfaces/components/dashboard/widget'

const emit = defineEmits<{
  'add-widget': [type: WidgetType]
  close: []
}>()

const { t } = useI18n()

interface CatalogItem {
  type: WidgetType
  icon: typeof ChartBarIcon
  labelKey: string
}

interface CatalogSection {
  sectionKey: string
  items: CatalogItem[]
}

const sections: CatalogSection[] = [
  {
    sectionKey: 'charts',
    items: [
      { type: 'bar-chart', icon: ChartBarIcon, labelKey: 'bar_chart' },
      { type: 'pie-chart', icon: ChartPieIcon, labelKey: 'pie_chart' },
      { type: 'time-series', icon: PresentationChartLineIcon, labelKey: 'time_series' },
    ],
  },
  {
    sectionKey: 'gauges',
    items: [{ type: 'gauge', icon: ArrowPathIcon, labelKey: 'gauge' }],
  },
  {
    sectionKey: 'status',
    items: [{ type: 'status-panel', icon: SignalIcon, labelKey: 'status_panel' }],
  },
  {
    sectionKey: 'indicators',
    items: [
      { type: 'indicator', icon: HashtagIcon, labelKey: 'indicator' },
      { type: 'totalizers', icon: CalculatorIcon, labelKey: 'totalizers' },
    ],
  },
  {
    sectionKey: 'alarms',
    items: [{ type: 'open-alarms', icon: BellAlertIcon, labelKey: 'open_alarms' }],
  },
]

const disabledItems = [
  { icon: ChartBarSquareIcon, labelKey: 'boxplot' },
  { icon: PresentationChartBarIcon, labelKey: 'histogram' },
]
</script>

<template>
  <aside class="flex w-72 shrink-0 flex-col border-l border-blue-200 bg-blue-50 text-black transition-all">
    <div class="flex items-center justify-between border-b border-blue-200 p-6">
      <h2 class="font-satoshi text-lg font-bold">
        {{ t('custom_dashboards.widget_catalog.title') }}
      </h2>
      <button type="button" class="text-blue-600 hover:text-blue-700" @click="emit('close')">
        <XMarkIcon class="h-5 w-5" />
      </button>
    </div>

    <div class="flex-1 overflow-y-auto p-6">
      <div v-for="section in sections" :key="section.sectionKey" class="mb-8">
        <h3 class="mb-4 text-xs font-bold uppercase tracking-widest text-blue-600">
          {{ t(`custom_dashboards.widget_catalog.sections.${section.sectionKey}`) }}
        </h3>
        <div class="flex flex-col gap-3">
          <button
            v-for="item in section.items"
            :key="item.type"
            type="button"
            class="group flex items-center justify-between border border-blue-200 bg-white p-3 text-left transition-all hover:bg-blue-100"
            @click="emit('add-widget', item.type)"
          >
            <div class="flex items-center gap-3">
              <component :is="item.icon" class="h-5 w-5 text-blue-600" />
              <span class="text-sm font-medium text-black">
                {{ t(`custom_dashboards.widget_catalog.items.${item.labelKey}`) }}
              </span>
            </div>
            <PlusIcon class="h-4 w-4 text-blue-600/60 group-hover:text-blue-600" />
          </button>
        </div>
      </div>

      <div class="mb-8">
        <h3 class="mb-4 text-xs font-bold uppercase tracking-widest text-blue-600">
          {{ t('custom_dashboards.widget_catalog.sections.statistics') }}
        </h3>
        <div class="flex flex-col gap-3">
          <span
            v-for="item in disabledItems"
            :key="item.labelKey"
            :title="t('custom_dashboards.widget_catalog.coming_soon')"
            class="block w-full"
          >
            <button
              type="button"
              disabled
              class="flex w-full cursor-not-allowed items-center justify-between border border-blue-200 bg-white p-3 text-left opacity-50"
            >
              <div class="flex items-center gap-3">
                <component :is="item.icon" class="h-5 w-5 text-blue-600" />
                <span class="text-sm font-medium text-black">
                  {{ t(`custom_dashboards.widget_catalog.items.${item.labelKey}`) }}
                </span>
              </div>
              <LockClosedIcon class="h-4 w-4 text-blue-600/60" />
            </button>
          </span>
        </div>
      </div>
    </div>
  </aside>
</template>
